import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Button, Form, Row, Col, OverlayTrigger, Popover } from 'react-bootstrap';
import SearchableSelect from '../components/SearchableSelect';
import api from '../services/api';
import './Spiglovani.css';

// "Hlavní" je pseudo-mutace reprezentující edition.mutation_id = NULL.
// Zobrazí se jen když pro médium neexistují žádné reálné mutace.
const HLAVNI_MUTATION = { id: 'hlavni', label: 'Hlavní' };

const PREFS_KEY = 'spiglovani_prefs';

const loadPrefs = () => {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
};

const PAGE_DEFAULTS = {
  name: '',
  page_type: 'K',
  margin_top: 16,
  margin_right: 11,
  margin_bottom: 16,
  margin_left: 11,
  width: 210,
  height: 297,
  columns: 4,
  column_gap: 4.0,
  color: '#ffffff',
  is_ad_page: false,
  ad_coverage_percent: 100,
  is_complete_page: false,
};

// Visible width of a page thumb (px). Used for mm→px scaling.
const THUMB_PX_W = 200;

const formatProjectDate = (dateStr) => {
  if (!dateStr) return '';
  const datePart = String(dateStr).substring(0, 10);
  const d = new Date(datePart + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

// Decide whether a hex color is "dark" enough that white text reads better
// than the default dark text. Uses perceived luminance (Rec. 709).
const isDarkColor = (hex) => {
  if (!hex || typeof hex !== 'string') return false;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(n => Number.isNaN(n))) return false;
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance < 0.55;
};

const formatMm = (value) => {
  if (value == null) return '';
  const n = Number(value);
  if (!isFinite(n)) return '';
  return n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDateTime = (s) => {
  if (!s) return '';
  const d = new Date(String(s).replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// Compute column width in mm for a page.
const colWidthMm = (page) => {
  const usable = Number(page.width) - Number(page.margin_left) - Number(page.margin_right);
  const cols = Math.max(1, Number(page.columns) || 1);
  const gap = Number(page.column_gap) || 0;
  return (usable - (cols - 1) * gap) / cols;
};

// How many columns an ad of given width spans on a given page.
const colSpanForWidth = (page, adWidthMm) => {
  if (!adWidthMm || adWidthMm <= 0) return 1;
  const cw = colWidthMm(page);
  const gap = Number(page.column_gap) || 0;
  // smallest N such that N*cw + (N-1)*gap >= adWidthMm
  for (let n = 1; n <= page.columns; n++) {
    if (n * cw + (n - 1) * gap + 0.01 >= adWidthMm) return n;
  }
  return Number(page.columns);
};

// Compute final col_start / col_span / y_mm / h_mm from a pointer (clientX/Y)
// hitting a page thumb. Respects page margins on both axes.
const computePlacement = (page, clientX, clientY, rect, ad) => {
  const scale = THUMB_PX_W / Number(page.width);
  const dropXmm = (clientX - rect.left) / scale;
  const dropYmm = (clientY - rect.top) / scale;
  const cw = colWidthMm(page);
  const gap = Number(page.column_gap) || 0;
  const xInsideUsable = Math.max(0, dropXmm - Number(page.margin_left));
  const colWithGap = cw + gap;
  let colStart = Math.floor(xInsideUsable / colWithGap) + 1;
  const adWidthMm = ad.width_mm || cw;
  const adHeightMm = ad.height_mm || cw;
  const colSpan = Math.min(Number(page.columns), colSpanForWidth(page, adWidthMm));
  colStart = Math.max(1, Math.min(colStart, Number(page.columns) - colSpan + 1));
  const usableH = Number(page.height) - Number(page.margin_top) - Number(page.margin_bottom);
  let yMm = Math.max(0, dropYmm - Number(page.margin_top));
  yMm = Math.max(0, Math.min(yMm, Math.max(0, usableH - adHeightMm)));
  return { colStart, colSpan, yMm, hMm: adHeightMm };
};

// Convert a placement (col-based) into absolute pixel coords inside the page thumb.
const placementToPixels = (page, placement) => {
  const scale = THUMB_PX_W / Number(page.width);
  const cw = colWidthMm(page);
  const gap = Number(page.column_gap) || 0;
  const xMmInUsable = (placement.colStart - 1) * (cw + gap);
  const wMm = placement.colSpan * cw + (placement.colSpan - 1) * gap;
  return {
    leftPx: (Number(page.margin_left) + xMmInUsable) * scale,
    topPx: (Number(page.margin_top) + placement.yMm) * scale,
    widthPx: wMm * scale,
    heightPx: placement.hMm * scale,
  };
};

function Spiglovani() {
  // Restore last picked selectors from localStorage (kept across reloads).
  const initialPrefsRef = useRef(loadPrefs());
  const prefs = initialPrefsRef.current;

  // Seed option lists with the saved {id,label} so the dropdown shows the
  // last picked value immediately, before the API list is fetched.
  const seedOptions = (id, label) =>
    id && label ? [{ id, label }] : [];

  // ── Selectors ────────────────────────────────────────────────────────
  const [mediaOptions, setMediaOptions] = useState(() => seedOptions(prefs.mediumId, prefs.mediumLabel));
  const [mediaLoading, setMediaLoading] = useState(false);
  const [selectedMediumId, setSelectedMediumId] = useState(() => prefs.mediumId || '');

  const [projectOptions, setProjectOptions] = useState(() => seedOptions(prefs.projectId, prefs.projectLabel));
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(() => prefs.projectId || '');

  const [selectedMutationId, setSelectedMutationId] = useState('');
  const [mutationOptions, setMutationOptions] = useState([HLAVNI_MUTATION]);
  const [mutationsLoading, setMutationsLoading] = useState(false);

  // Pixel offset of the cursor inside the dragged element at dragstart — so the
  // drop preview lines up with where the user grabbed the ad, instead of using
  // the cursor as the ad's top-left corner.
  const dragGrabOffsetRef = useRef({ x: 0, y: 0 });

  // Remembers the last medium we loaded projects for. Used to detect a genuine
  // medium change vs. a re-run of the effect (e.g. React StrictMode double-invoke).
  // Idempotent so we don't accidentally wipe the restored project on the second run.
  const prevMediumIdRef = useRef(null);

  // ── Edition state ────────────────────────────────────────────────────
  const [editionId, setEditionId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [pages, setPages] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [lineAds, setLineAds] = useState([]);
  const [loadingEdition, setLoadingEdition] = useState(false);
  const [highlightedPositionId, setHighlightedPositionId] = useState(null);

  // ── UI state ─────────────────────────────────────────────────────────
  const [pageModal, setPageModal] = useState(null); // { mode: 'create'|'edit', page }
  const [moveModal, setMoveModal] = useState(null); // { page, newPosition }
  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [cloneSourceProjectId, setCloneSourceProjectId] = useState('');
  const [cloneSourceOptions, setCloneSourceOptions] = useState([]);
  const [cloneSourceLoading, setCloneSourceLoading] = useState(false);
  const [cloneSubmitting, setCloneSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, body, onConfirm, variant }
  const [dropTargetPageId, setDropTargetPageId] = useState(null);
  const [draggedAd, setDraggedAd] = useState(null);
  // When the dragged item is an already-placed position (not a fresh ad from the
  // sidebar), this holds its id. We use it to gate "drop to unassigned = delete"
  // and to suppress accidental duplicate creation when dropped on another page.
  const [draggedPositionId, setDraggedPositionId] = useState(null);
  const [dragPreview, setDragPreview] = useState(null); // { pageId, leftPx, topPx, widthPx, heightPx, category }
  const [unassignedDropActive, setUnassignedDropActive] = useState(false);
  const [expandedBoxes, setExpandedBoxes] = useState({
    unassigned: true,
    placed: false,
    line: false,
  });
  const toggleBox = (key) => setExpandedBoxes(s => ({ ...s, [key]: !s[key] }));

  const allSelected = selectedMediumId && selectedProjectId && selectedMutationId;
  const currentVersion = useMemo(
    () => versions.find(v => v.id === activeVersionId) || null,
    [versions, activeVersionId]
  );
  const activeVersion = useMemo(() => versions.find(v => v.type === 'active') || null, [versions]);
  const isViewingActive = currentVersion?.type === 'active';

  // ── Load media ───────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setMediaLoading(true);
    api.getMedia({ limit: 500, sort: 'name', order: 'asc' })
      .then(res => {
        if (cancelled) return;
        setMediaOptions((res.data || []).map(m => ({ id: m.id, label: m.name })));
      })
      .catch(() => { if (!cancelled) setMediaOptions([]); })
      .finally(() => { if (!cancelled) setMediaLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ── Load projects on medium change ──────────────────────────────────
  useEffect(() => {
    if (!selectedMediumId) {
      setProjectOptions([]);
      setSelectedProjectId('');
      setSelectedMutationId('');
      prevMediumIdRef.current = null;
      return;
    }

    // Only reset the picked project when the medium ACTUALLY changed (a non-null
    // previous value that's different from the current one). This stays idempotent
    // across re-runs of the effect — e.g. React StrictMode double-invoke in dev.
    const isMediumChange = prevMediumIdRef.current !== null
      && String(prevMediumIdRef.current) !== String(selectedMediumId);
    if (isMediumChange) {
      setSelectedProjectId('');
      setSelectedMutationId('');
    }
    prevMediumIdRef.current = selectedMediumId;

    // Preserve the currently selected project in the option list — even if it's
    // outside the top-15 window — unless we just changed mediums (then the old
    // id is irrelevant).
    const projectIdToPreserve = isMediumChange ? null : selectedProjectId;

    let cancelled = false;
    setProjectsLoading(true);
    api.getMediumProjects(selectedMediumId)
      .then(res => {
        if (cancelled) return;
        const full = res.data || [];
        const buildOpt = (p) => {
          const datePart = formatProjectDate(p.date);
          return { id: p.id, label: datePart ? `${p.name} – ${datePart}` : p.name };
        };
        let opts = full.slice(0, 15).map(buildOpt);

        if (projectIdToPreserve && !opts.find(o => String(o.id) === String(projectIdToPreserve))) {
          const match = full.find(p => String(p.id) === String(projectIdToPreserve));
          if (match) opts = [buildOpt(match), ...opts];
        }
        setProjectOptions(opts);
      })
      .catch(() => { if (!cancelled) setProjectOptions([]); })
      .finally(() => { if (!cancelled) setProjectsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMediumId]);

  // Mutace navázané na vybrané médium. Pokud alespoň jedna existuje, zobrazíme
  // pouze reálné mutace; teprve když pro médium žádná není, fallbackneme na
  // pseudo-mutaci „Hlavní".
  useEffect(() => {
    if (!selectedMediumId) {
      setMutationOptions([HLAVNI_MUTATION]);
      return;
    }
    let cancelled = false;
    setMutationsLoading(true);
    api.getMediumMutations(selectedMediumId)
      .then(res => {
        if (cancelled) return;
        const list = (res.data || []).map(m => ({ id: m.id, label: m.name }));
        setMutationOptions(list.length > 0 ? list : [HLAVNI_MUTATION]);
      })
      .catch(() => { if (!cancelled) setMutationOptions([HLAVNI_MUTATION]); })
      .finally(() => { if (!cancelled) setMutationsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedMediumId]);

  // Auto-pick mutace: bez projektu prázdné; jinak první dostupná mutace
  // (případně „Hlavní" fallback). Reaguje i na změnu seznamu mutací.
  useEffect(() => {
    if (!selectedProjectId) {
      setSelectedMutationId('');
      return;
    }
    const stillValid = mutationOptions.some(o => String(o.id) === String(selectedMutationId));
    if (!stillValid) {
      setSelectedMutationId(mutationOptions[0]?.id ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, mutationOptions]);

  // Persist selectors (incl. labels) so the editor reopens on the same edition
  // after a reload — labels let the dropdown render before the API responds.
  useEffect(() => {
    try {
      const mediumOpt = mediaOptions.find(o => String(o.id) === String(selectedMediumId));
      const projectOpt = projectOptions.find(o => String(o.id) === String(selectedProjectId));
      localStorage.setItem(PREFS_KEY, JSON.stringify({
        mediumId: selectedMediumId || null,
        mediumLabel: mediumOpt?.label || null,
        projectId: selectedProjectId || null,
        projectLabel: projectOpt?.label || null,
      }));
    } catch {
      // ignore quota / disabled storage
    }
  }, [selectedMediumId, selectedProjectId, mediaOptions, projectOptions]);

  // ── Lookup edition when all three are picked ─────────────────────────
  useEffect(() => {
    if (!allSelected) {
      setEditionId(null);
      setVersions([]);
      setActiveVersionId(null);
      setPages([]);
      setUnassigned([]);
      return;
    }
    let cancelled = false;
    setLoadingEdition(true);
    api.lookupEdition({
      mediumId: selectedMediumId,
      projectId: selectedProjectId,
      // Pseudo-mutace „Hlavní" = prázdná hodnota → edition.mutation_id NULL.
      mutation: selectedMutationId === HLAVNI_MUTATION.id ? '' : selectedMutationId,
    })
      .then(res => {
        if (cancelled) return;
        const data = res.data || {};
        setEditionId(data.edition?.id || null);
        const vs = data.versions || [];
        setVersions(vs);
        const active = vs.find(v => v.type === 'active');
        setActiveVersionId(active?.id || null);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Edition lookup failed:', err);
      })
      .finally(() => { if (!cancelled) setLoadingEdition(false); });
    return () => { cancelled = true; };
  }, [allSelected, selectedMediumId, selectedProjectId, selectedMutationId]);

  // ── Load pages + unassigned when version changes ─────────────────────
  const loadPagesAndUnassigned = useCallback(async () => {
    if (!activeVersionId) {
      setPages([]);
      setUnassigned([]);
      setLineAds([]);
      return;
    }
    try {
      const [pagesRes, unassignedRes] = await Promise.all([
        api.getEditionPages(activeVersionId),
        api.getEditionUnassigned(activeVersionId),
      ]);
      setPages(pagesRes.data || []);
      const u = unassignedRes.data || {};
      setUnassigned(Array.isArray(u) ? u : (u.unassigned || []));
      setLineAds(Array.isArray(u) ? [] : (u.line_ads || []));
    } catch (e) {
      console.error('Failed to load pages/unassigned:', e);
      setPages([]);
      setUnassigned([]);
      setLineAds([]);
    }
  }, [activeVersionId]);

  useEffect(() => { loadPagesAndUnassigned(); }, [loadPagesAndUnassigned]);

  // Flat list of placed ads across all pages — used in the "Zavedené inzeráty" box.
  const placedAds = useMemo(() => {
    const out = [];
    for (const p of pages) {
      for (const pos of (p.positions || [])) {
        out.push({
          position_id: pos.id,
          page_id: p.id,
          page_number: p.page_number,
          ...(pos.ad || {}),
        });
      }
    }
    return out;
  }, [pages]);

  // Scroll the position into view + flash a highlight ring for ~1.5 s.
  const highlightTimerRef = useRef(null);
  const focusPosition = useCallback((positionId) => {
    if (!positionId) return;
    const el = document.querySelector(`[data-position-id="${positionId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedPositionId(positionId);
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightedPositionId(null), 1500);
  }, []);
  useEffect(() => () => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
  }, []);

  // Inzerce — kolik % máme z té max. možné plochy reálně zaplněno inzeráty.
  // Bere v úvahu jen stránky označené jako "Inzertní strana"; pro každou
  // takovou stránku: max = usable_area × ad_coverage_percent / 100.
  // Skutečné = součet ploch umístěných inzerátů (col_span × col_width + mezery × h_mm).
  const adCoverage = useMemo(() => {
    let totalMax = 0;
    let totalPlaced = 0;
    for (const page of pages) {
      const isAd = Number(page.is_ad_page) === 1 || page.is_ad_page === true;
      const pct = Number(page.ad_coverage_percent) || 0;
      if (!isAd || pct <= 0) continue;

      const usableW = Math.max(0, Number(page.width) - Number(page.margin_left) - Number(page.margin_right));
      const usableH = Math.max(0, Number(page.height) - Number(page.margin_top) - Number(page.margin_bottom));
      totalMax += usableW * usableH * (pct / 100);

      const cw = colWidthMm(page);
      const gap = Number(page.column_gap) || 0;
      for (const pos of (page.positions || [])) {
        const span = Number(pos.col_span) || 1;
        const adW = span * cw + (span - 1) * gap;
        const adH = Number(pos.h_mm) || 0;
        if (adW > 0 && adH > 0) totalPlaced += adW * adH;
      }
    }
    if (totalMax <= 0) return null;
    return Math.round((totalPlaced / totalMax) * 1000) / 10; // 1 desetinné místo
  }, [pages]);

  // ── Refresh versions list ────────────────────────────────────────────
  const refreshVersions = useCallback(async () => {
    if (!editionId) return;
    try {
      const res = await api.getEditionVersions(editionId);
      setVersions(res.data || []);
    } catch (e) {
      console.error('Failed to refresh versions:', e);
    }
  }, [editionId]);

  // ── Draft create / publish / discard ─────────────────────────────────
  const handleOpenDraftModal = () => {
    const today = new Date();
    const stamp = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.`;
    setDraftName(`Draft ${stamp}`);
    setDraftModalOpen(true);
  };

  const handleOpenCloneModal = () => {
    setCloneSourceProjectId('');
    setCloneSourceOptions([]);
    setCloneModalOpen(true);
    if (!selectedMediumId) return;

    setCloneSourceLoading(true);
    api.getMediumProjects(selectedMediumId)
      .then(res => {
        const all = res.data || [];
        const buildOpt = (p) => {
          const datePart = formatProjectDate(p.date);
          return { id: p.id, label: datePart ? `${p.name} – ${datePart}` : p.name };
        };
        // Exclude the current project — cloning from the same edition makes no sense.
        const opts = all
          .filter(p => String(p.id) !== String(selectedProjectId))
          .map(buildOpt);
        setCloneSourceOptions(opts);
      })
      .catch(() => setCloneSourceOptions([]))
      .finally(() => setCloneSourceLoading(false));
  };

  const handleConfirmClone = async () => {
    if (!editionId || !cloneSourceProjectId) return;
    const sourceOpt = cloneSourceOptions.find(o => String(o.id) === String(cloneSourceProjectId));
    const today = new Date();
    const stamp = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.`;
    const sourceLabel = sourceOpt?.label || `projekt #${cloneSourceProjectId}`;
    const autoName = `Draft z „${sourceLabel}" – ${stamp}`;
    setCloneSubmitting(true);
    try {
      const res = await api.cloneEditionFromProject(editionId, cloneSourceProjectId, autoName);
      const newId = res.data?.version_id;
      setVersions(res.data?.versions || []);
      if (newId) setActiveVersionId(newId);
      setCloneModalOpen(false);
      setCloneSourceProjectId('');
      setCloneSourceOptions([]);
    } catch (e) {
      console.error('Clone-from-project failed:', e);
      alert('Nakopírování z vybraného projektu se nezdařilo.');
    } finally {
      setCloneSubmitting(false);
    }
  };

  const handleCreateDraft = async () => {
    if (!editionId || !draftName.trim()) return;
    try {
      const res = await api.duplicateEditionVersion(editionId, draftName.trim());
      const newId = res.data?.version_id;
      setVersions(res.data?.versions || []);
      if (newId) setActiveVersionId(newId);
      setDraftModalOpen(false);
      setDraftName('');
    } catch (e) {
      console.error('Failed to create draft:', e);
      alert('Nepodařilo se vytvořit draft.');
    }
  };

  const handlePublishDraft = () => {
    if (!currentVersion || currentVersion.type !== 'draft') return;
    setConfirmDialog({
      title: 'Publikovat draft',
      body: `Publikováním přepíšete aktivní verzi obsahem draftu „${currentVersion.name}". Draft poté zmizí. Pokračovat?`,
      variant: 'success',
      confirmLabel: 'Publikovat',
      onConfirm: async () => {
        try {
          const res = await api.publishEditionVersion(currentVersion.id);
          setVersions(res.data?.versions || []);
          const newActiveId = res.data?.active_version_id;
          if (newActiveId) setActiveVersionId(newActiveId);
        } catch (e) {
          console.error('Publish failed:', e);
          alert('Publikování se nezdařilo.');
        } finally {
          setConfirmDialog(null);
        }
      },
    });
  };

  const handleDiscardDraft = () => {
    if (!currentVersion || currentVersion.type !== 'draft') return;
    setConfirmDialog({
      title: 'Zahodit draft',
      body: `Opravdu chcete zahodit draft „${currentVersion.name}"? Tato akce je nevratná.`,
      variant: 'danger',
      confirmLabel: 'Zahodit',
      onConfirm: async () => {
        try {
          const res = await api.deleteEditionVersion(currentVersion.id);
          setVersions(res.data?.versions || []);
          if (activeVersion) setActiveVersionId(activeVersion.id);
        } catch (e) {
          console.error('Discard failed:', e);
          alert('Smazání draftu se nezdařilo.');
        } finally {
          setConfirmDialog(null);
        }
      },
    });
  };

  // ── Page modal ───────────────────────────────────────────────────────
  const openCreatePageModal = () => {
    setPageModal({ mode: 'create', page: { ...PAGE_DEFAULTS } });
  };

  const openEditPageModal = (page) => {
    setPageModal({ mode: 'edit', page: { ...page } });
  };

  const handlePageFieldChange = (field, value) => {
    setPageModal(prev => prev ? { ...prev, page: { ...prev.page, [field]: value } } : prev);
  };

  const handleSavePage = async () => {
    if (!pageModal || !activeVersionId) return;
    const isAdPage = !!pageModal.page.is_ad_page;
    const payload = {
      name: pageModal.page.name,
      page_type: pageModal.page.page_type,
      margin_top: Number(pageModal.page.margin_top),
      margin_right: Number(pageModal.page.margin_right),
      margin_bottom: Number(pageModal.page.margin_bottom),
      margin_left: Number(pageModal.page.margin_left),
      width: Number(pageModal.page.width),
      height: Number(pageModal.page.height),
      columns: Number(pageModal.page.columns) || 4,
      column_gap: Number(pageModal.page.column_gap),
      color: pageModal.page.color,
      is_ad_page: isAdPage ? 1 : 0,
      ad_coverage_percent: isAdPage
        ? Math.max(0, Math.min(100, Number(pageModal.page.ad_coverage_percent) || 0))
        : 0,
      is_complete_page: pageModal.page.is_complete_page ? 1 : 0,
    };
    try {
      if (pageModal.mode === 'create') {
        await api.createEditionPage(activeVersionId, payload);
      } else {
        await api.updateEditionPage(pageModal.page.id, payload);
      }
      await loadPagesAndUnassigned();
      setPageModal(null);
    } catch (e) {
      console.error('Save page failed:', e);
      alert('Uložení stránky se nezdařilo.');
    }
  };

  const openMoveModal = (page) => {
    setMoveModal({ page, newPosition: page.page_number });
  };

  const handleConfirmMove = async () => {
    if (!moveModal) return;
    const target = Math.max(1, Math.min(pages.length, Number(moveModal.newPosition) || 1));
    if (target === moveModal.page.page_number) {
      setMoveModal(null);
      return;
    }
    try {
      await api.moveEditionPage(moveModal.page.id, target);
      await loadPagesAndUnassigned();
      setMoveModal(null);
    } catch (e) {
      console.error('Move page failed:', e);
      alert('Přesun stránky se nezdařil.');
    }
  };

  const handleDeletePage = () => {
    if (!pageModal || pageModal.mode !== 'edit') return;
    const target = pageModal.page;
    setConfirmDialog({
      title: 'Smazat stránku',
      body: `Opravdu chcete smazat stránku ${target.page_number}? Inzeráty, které na ní byly umístěné, se vrátí do nezavedených.`,
      variant: 'danger',
      confirmLabel: 'Smazat',
      onConfirm: async () => {
        try {
          await api.deleteEditionPage(target.id);
          await loadPagesAndUnassigned();
          setPageModal(null);
        } catch (e) {
          console.error('Delete page failed:', e);
          alert('Smazání stránky se nezdařilo.');
        } finally {
          setConfirmDialog(null);
        }
      },
    });
  };

  // ── Drag & drop ──────────────────────────────────────────────────────
  const resetDragState = () => {
    setDraggedAd(null);
    setDraggedPositionId(null);
    setDragPreview(null);
    setDropTargetPageId(null);
    setUnassignedDropActive(false);
    dragGrabOffsetRef.current = { x: 0, y: 0 };
  };

  // From sidebar → page (creates a new position)
  const onDragStartUnassigned = (e, ad) => {
    if (isViewingActive) { e.preventDefault(); return; }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({ kind: 'new', ad }));
    // No grab offset — sidebar item size is unrelated to the ad's real size.
    dragGrabOffsetRef.current = { x: 0, y: 0 };
    setDraggedAd(ad);
    setDraggedPositionId(null);
  };

  // From a placed position → drag to move (same / another page) or to unassigned (delete).
  const onDragStartPosition = (e, pos) => {
    if (isViewingActive) { e.preventDefault(); return; }
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({
      kind: 'position',
      positionId: pos.id,
      ad: pos.ad,
    }));
    // Capture where inside the dragged ad the user grabbed it — used to keep
    // the drop preview aligned with the cursor instead of jumping by the ad's
    // top-left corner.
    const rect = e.currentTarget.getBoundingClientRect();
    dragGrabOffsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    setDraggedAd(pos.ad || null);
    setDraggedPositionId(pos.id);
  };

  const onDragEndAny = () => {
    resetDragState();
  };

  const onDragOverPage = (e, page) => {
    if (isViewingActive) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropTargetPageId !== page.id) setDropTargetPageId(page.id);
    if (!draggedAd) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const off = dragGrabOffsetRef.current;
    const placement = computePlacement(page, e.clientX - off.x, e.clientY - off.y, rect, draggedAd);
    const pixels = placementToPixels(page, placement);
    setDragPreview({
      pageId: page.id,
      ...pixels,
      category: draggedAd.color_category || 'placena',
      formatLabel: draggedAd.format_label,
    });
  };

  const onDragLeavePage = (e) => {
    // Only clear if pointer moved outside the thumb (not into a child element).
    const related = e.relatedTarget;
    if (related && e.currentTarget.contains(related)) return;
    setDropTargetPageId(null);
    setDragPreview(null);
  };

  const onDropToPage = async (e, page) => {
    if (isViewingActive) return;
    e.preventDefault();
    const movingPositionId = draggedPositionId;
    // Snapshot the grab offset before resetDragState() wipes it.
    const off = { ...dragGrabOffsetRef.current };
    resetDragState();

    const json = e.dataTransfer.getData('application/json');
    if (!json) return;
    let payload;
    try { payload = JSON.parse(json); } catch { return; }
    const ad = payload.ad || payload; // back-compat if older shape lingers
    if (!ad || !ad.booking_item_id) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const placement = computePlacement(page, e.clientX - off.x, e.clientY - off.y, rect, ad);

    try {
      if (movingPositionId) {
        // Move an existing position — within the same page or to another page.
        await api.updateEditionPosition(movingPositionId, {
          page_id: page.id,
          col_start: placement.colStart,
          col_span: placement.colSpan,
          y_mm: Number(placement.yMm.toFixed(2)),
          h_mm: Number(placement.hMm.toFixed(2)),
        });
      } else {
        await api.createEditionPosition(page.id, {
          booking_item_id: ad.booking_item_id,
          col_start: placement.colStart,
          col_span: placement.colSpan,
          y_mm: Number(placement.yMm.toFixed(2)),
          h_mm: Number(placement.hMm.toFixed(2)),
        });
      }
      await loadPagesAndUnassigned();
    } catch (err) {
      console.error('Drop failed:', err);
      alert('Umístění inzerátu se nezdařilo.');
    }
  };

  // Drop on the unassigned panel — only acts when an existing position is dragged.
  const onDragOverUnassigned = (e) => {
    if (isViewingActive) return;
    if (!draggedPositionId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!unassignedDropActive) setUnassignedDropActive(true);
  };

  const onDragLeaveUnassigned = (e) => {
    if (!draggedPositionId) return;
    const related = e.relatedTarget;
    if (related && e.currentTarget.contains(related)) return;
    setUnassignedDropActive(false);
  };

  const onDropToUnassigned = async (e) => {
    if (isViewingActive) return;
    if (!draggedPositionId) return;
    e.preventDefault();
    const posId = draggedPositionId;
    resetDragState();
    try {
      await api.deleteEditionPosition(posId);
      await loadPagesAndUnassigned();
    } catch (err) {
      console.error('Remove via drop failed:', err);
      alert('Vrácení inzerátu do nezavedených se nezdařilo.');
    }
  };

  // ── Rendering helpers ────────────────────────────────────────────────
  const renderPositions = (page) => {
    const scale = THUMB_PX_W / Number(page.width);
    const cw = colWidthMm(page);
    const gap = Number(page.column_gap) || 0;
    return (page.positions || []).map(pos => {
      const xMmInUsable = (Number(pos.col_start) - 1) * (cw + gap);
      const wMm = Number(pos.col_span) * cw + (Number(pos.col_span) - 1) * gap;
      const leftPx = (Number(page.margin_left) + xMmInUsable) * scale;
      const topPx = (Number(page.margin_top) + Number(pos.y_mm)) * scale;
      const widthPx = wMm * scale;
      const heightPx = Number(pos.h_mm) * scale;
      const ad = pos.ad || {};
      const cat = ad.color_category || 'placena';
      const isDragging = draggedPositionId === pos.id;
      const isHighlighted = highlightedPositionId === pos.id;
      const showAdvertiser = ad.advertiser_name && ad.advertiser_name !== ad.customer_name;

      const popover = (
        <Popover className="ad-popover">
          <Popover.Body>
            {ad.item_number && <div><strong>{ad.item_number}</strong></div>}
            {ad.customer_name && <div>{ad.customer_name}</div>}
            {showAdvertiser && <div>{ad.advertiser_name}</div>}
            {(ad.width_mm || ad.height_mm) && (
              <>
                <div className="mt-2">
                  {ad.width_mm != null && <>Š: {formatMm(ad.width_mm)} mm<br /></>}
                  {ad.height_mm != null && <>V: {formatMm(ad.height_mm)} mm</>}
                </div>
              </>
            )}
            {ad.external_id != null && (
              <div className="mt-2 text-secondary">ID: {ad.external_id}</div>
            )}
          </Popover.Body>
        </Popover>
      );

      return (
        <OverlayTrigger
          key={pos.id}
          placement="top"
          delay={{ show: 250, hide: 0 }}
          overlay={popover}
        >
          <div
            className={`pos-ad ${cat}${isDragging ? ' is-dragging' : ''}${isHighlighted ? ' is-highlighted' : ''}${isViewingActive ? ' is-readonly' : ''}`}
            data-position-id={pos.id}
            style={{ left: `${leftPx}px`, top: `${topPx}px`, width: `${widthPx}px`, height: `${heightPx}px` }}
            draggable={!isViewingActive}
            onDragStart={(e) => onDragStartPosition(e, pos)}
            onDragEnd={onDragEndAny}
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="pos-ad-content">
              {ad.item_number && <div className="pos-ad-line item-number">{ad.item_number}</div>}
              {ad.customer_name && <div className="pos-ad-line">{ad.customer_name}</div>}
              {showAdvertiser && <div className="pos-ad-line">{ad.advertiser_name}</div>}
            </div>
          </div>
        </OverlayTrigger>
      );
    });
  };

  // Render the column grid (gray rectangles) inside the usable area.
  const renderColumns = (page) => {
    const scale = THUMB_PX_W / Number(page.width);
    const cw = colWidthMm(page);
    const gap = Number(page.column_gap) || 0;
    const cols = Math.max(1, Number(page.columns) || 1);
    const usableHmm = Math.max(0, Number(page.height) - Number(page.margin_top) - Number(page.margin_bottom));
    const items = [];
    for (let i = 0; i < cols; i++) {
      const xMm = Number(page.margin_left) + i * (cw + gap);
      items.push(
        <div
          key={i}
          className="page-column"
          style={{
            left: `${xMm * scale}px`,
            top: `${Number(page.margin_top) * scale}px`,
            width: `${cw * scale}px`,
            height: `${usableHmm * scale}px`,
          }}
        />
      );
    }
    return items;
  };

  const renderPage = (page) => {
    const scale = THUMB_PX_W / Number(page.width);
    const heightPx = Number(page.height) * scale;
    const isDropTarget = dropTargetPageId === page.id;
    const pageColor = page.color || '#ffffff';
    const isWhite = pageColor.toLowerCase() === '#ffffff' || pageColor.toLowerCase() === '#fff';

    return (
      <div
        key={page.id}
        className={`thumb shadow-sm${isDropTarget ? ' drop-target' : ''}`}
        style={{
          width: `${THUMB_PX_W}px`,
          height: `${heightPx}px`,
        }}
        onDragOver={(e) => onDragOverPage(e, page)}
        onDragLeave={onDragLeavePage}
        onDrop={(e) => onDropToPage(e, page)}
      >
        {(Number(page.is_complete_page) === 1 || page.is_complete_page === true) && (
          <span className="km-tag km-tag-complete" title="Kompletová strana">K</span>
        )}
        {!isViewingActive && (
          <>
            <button
              type="button"
              className="page-edit-btn"
              title="Upravit stranu"
              onClick={(ev) => { ev.stopPropagation(); openEditPageModal(page); }}
            >
              <i className="bi bi-pencil" />
            </button>
            <button
              type="button"
              className="page-move-btn"
              title="Přesunout stranu"
              onClick={(ev) => { ev.stopPropagation(); openMoveModal(page); }}
            >
              <i className="bi bi-arrows-move" />
            </button>
          </>
        )}
        {page.name && (
          <div
            className="page-name"
            style={!isWhite ? {
              backgroundColor: pageColor,
              color: isDarkColor(pageColor) ? '#ffffff' : undefined,
            } : undefined}
          >
            {page.name}
          </div>
        )}
        {/* Column grid (gray rectangles within the margins) */}
        {renderColumns(page)}
        {/* Usable area outline (margins) */}
        <div
          className="usable-area"
          style={{
            left: `${Number(page.margin_left) * scale}px`,
            top: `${Number(page.margin_top) * scale}px`,
            right: `${Number(page.margin_right) * scale}px`,
            bottom: `${Number(page.margin_bottom) * scale}px`,
          }}
        />
        {renderPositions(page)}
        {dragPreview && dragPreview.pageId === page.id && (
          <div
            className={`pos-ad drag-preview ${dragPreview.category}`}
            style={{
              left: `${dragPreview.leftPx}px`,
              top: `${dragPreview.topPx}px`,
              width: `${dragPreview.widthPx}px`,
              height: `${dragPreview.heightPx}px`,
            }}
          >
            <span className="pos-ad-label">{dragPreview.formatLabel || ''}</span>
          </div>
        )}
        <div className="pnum">{page.page_number}</div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className="spiglovani-page">
      <div className="container-fluid py-3">

        {/* SELEKTORY */}
        <div className="spiglovani-selectors mb-3">
          <div className="sel">
            <label>Médium:</label>
            <SearchableSelect
              options={mediaOptions}
              value={selectedMediumId}
              onChange={(id) => setSelectedMediumId(id)}
              placeholder="Hledat médium…"
              emptyLabel={mediaLoading ? 'Načítám…' : '— vyberte —'}
            />
          </div>
          <div className="sel">
            <label>Projekt:</label>
            <SearchableSelect
              options={projectOptions}
              value={selectedProjectId}
              onChange={(id) => setSelectedProjectId(id)}
              placeholder="Hledat projekt…"
              emptyLabel={
                !selectedMediumId ? '— nejprve médium —' :
                projectsLoading ? 'Načítám…' : '— vyberte —'
              }
            />
          </div>
          <div className="sel">
            <label>Mutace:</label>
            <SearchableSelect
              options={mutationOptions}
              value={selectedMutationId}
              onChange={(id) => setSelectedMutationId(id)}
              placeholder="Hledat mutaci…"
              emptyLabel={
                !selectedProjectId ? '— nejprve projekt —' :
                mutationsLoading ? 'Načítám…' : '— vyberte —'
              }
            />
          </div>
          {/* Export PDF je dočasně skrytý
          <div className="sel-actions">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!editionId || !activeVersionId || pages.length === 0}
              onClick={() => {
                if (!editionId || !activeVersionId) return;
                window.open(api.getEditionPdfUrl(editionId, activeVersionId), '_blank');
              }}
            >
              <i className="bi bi-file-earmark-pdf me-1" />Export PDF
            </Button>
          </div>
          */}
        </div>

        <div className="spiglovani-content-wrap">
        <div className={`spiglovani-content${allSelected ? '' : ' is-empty'}`} aria-hidden={!allSelected}>

        <div className="row g-3">

          {/* LEVÝ PANEL */}
          <aside className="col-12 col-lg-4 col-xxl-3">
            {/* Pokrytí */}
            <div className="card shadow-sm mb-3">
              <div className="card-header"><strong>Pokrytí vydání</strong></div>
              <ul className="list-group list-group-flush">
                <li className="list-group-item d-flex justify-content-between">
                  <span>Inzerce</span>
                  <strong>
                    {adCoverage == null
                      ? '—'
                      : `${adCoverage.toLocaleString('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`}
                  </strong>
                </li>
              </ul>
            </div>

            {/* Nezavedené inzeráty */}
            <div className="card shadow-sm mb-3">
              <div
                className="card-header d-flex align-items-center collapsible-header"
                onClick={() => toggleBox('unassigned')}
              >
                <i className={`bi ${expandedBoxes.unassigned ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`} />
                <strong>Nezavedené inzeráty</strong>
                <span className="badge rounded-pill text-bg-secondary ms-auto">{unassigned.length}</span>
              </div>

              {expandedBoxes.unassigned && (
              <div
                className={`list-group list-group-flush unassigned-list${unassignedDropActive ? ' drop-remove' : ''}`}
                onDragOver={onDragOverUnassigned}
                onDragLeave={onDragLeaveUnassigned}
                onDrop={onDropToUnassigned}
              >
                {unassignedDropActive && (
                  <div className="unassigned-drop-hint">
                    <i className="bi bi-trash3" />
                    <span>Pustit pro vrácení mezi nezavedené</span>
                  </div>
                )}
                {unassigned.length === 0 && (
                  <div className="list-group-item text-secondary small">Žádné nezavedené inzeráty.</div>
                )}
                {unassigned.map(ad => (
                  <div
                    key={ad.booking_item_id}
                    className={`list-group-item list-group-item-action unassigned-item ${ad.color_category}${isViewingActive ? ' is-readonly' : ''}`}
                    draggable={!isViewingActive}
                    onDragStart={(e) => onDragStartUnassigned(e, ad)}
                    onDragEnd={onDragEndAny}
                  >
                    <div className="d-flex align-items-start">
                      <div className="me-auto">
                        <div className="meta">
                          <strong>{ad.item_number || `#${ad.booking_item_id}`}</strong>
                          {ad.customer_name && <> · {ad.customer_name}</>}
                        </div>
                        <small>
                          {ad.booking_number ? `obj. ${ad.booking_number}` : ''}
                          {ad.width_mm && ad.height_mm ? ` · ${Math.round(ad.width_mm)}×${Math.round(ad.height_mm)} mm` : ''}
                        </small>
                      </div>
                      {ad.format_label && (
                        <span className={`badge badge-format ms-2 cat-${ad.color_category}`}>{ad.format_label}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>

            {/* Zavedené inzeráty */}
            <div className="card shadow-sm mb-3">
              <div
                className="card-header d-flex align-items-center collapsible-header"
                onClick={() => toggleBox('placed')}
              >
                <i className={`bi ${expandedBoxes.placed ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`} />
                <strong>Zavedené inzeráty</strong>
                <span className="badge rounded-pill text-bg-secondary ms-auto">{placedAds.length}</span>
              </div>
              {expandedBoxes.placed && (
                <div className="list-group list-group-flush placed-list">
                  {placedAds.length === 0 && (
                    <div className="list-group-item text-secondary small">Žádné inzeráty zatím nejsou zavedené.</div>
                  )}
                  {placedAds.map(ad => (
                    <div
                      key={ad.position_id}
                      className={`list-group-item list-group-item-action placed-item ${ad.color_category}`}
                      onClick={() => focusPosition(ad.position_id)}
                    >
                      <div className="d-flex align-items-start">
                        <div className="me-auto">
                          <div className="meta">
                            <strong>{ad.item_number || `#${ad.booking_item_id}`}</strong>
                            {ad.customer_name && <> · {ad.customer_name}</>}
                          </div>
                          <small>
                            str. {ad.page_number}
                            {ad.width_mm && ad.height_mm ? ` · ${Math.round(ad.width_mm)}×${Math.round(ad.height_mm)} mm` : ''}
                          </small>
                        </div>
                        {ad.format_label && (
                          <span className={`badge badge-format ms-2 cat-${ad.color_category}`}>{ad.format_label}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Řádková inzerce */}
            <div className="card shadow-sm">
              <div
                className="card-header d-flex align-items-center collapsible-header"
                onClick={() => toggleBox('line')}
              >
                <i className={`bi ${expandedBoxes.line ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`} />
                <strong>Řádková inzerce</strong>
                <span className="badge rounded-pill text-bg-secondary ms-auto">{lineAds.length}</span>
              </div>
              {expandedBoxes.line && (
                <div className="list-group list-group-flush line-ads-list">
                  {lineAds.length === 0 && (
                    <div className="list-group-item text-secondary small">Žádná řádková inzerce.</div>
                  )}
                  {lineAds.map(ad => (
                    <div
                      key={ad.booking_item_id}
                      className={`list-group-item list-group-item-action line-ads-item ${ad.color_category}${isViewingActive ? ' is-readonly' : ''}`}
                      draggable={!isViewingActive}
                      onDragStart={(e) => onDragStartUnassigned(e, ad)}
                      onDragEnd={onDragEndAny}
                    >
                      <div className="meta">
                        <strong>{ad.item_number || `#${ad.booking_item_id}`}</strong>
                        {ad.customer_name && <> · {ad.customer_name}</>}
                      </div>
                      {ad.booking_number && <small>obj. {ad.booking_number}</small>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>

          {/* PRAVÝ PANEL */}
          <main className="col-12 col-lg-8 col-xxl-9">

            {/* ============ DRAFT TABS ============ */}
            <div className="draft-tabs">
              {versions.map(v => {
                const isActive = v.id === activeVersionId;
                const kind = v.type === 'active' ? 'active' : 'draft';
                return (
                  <div
                    key={v.id}
                    className={`draft-tab${isActive ? ' active' : ''}`}
                    title={v.type === 'active' ? 'Publikovaná aktivní verze' : 'Rozpracovaný draft'}
                    onClick={() => setActiveVersionId(v.id)}
                  >
                    <span className={`draft-dot ${kind}`}></span>
                    <span>{v.type === 'active' ? 'Aktivní verze' : v.name}</span>
                    {v.type === 'draft' && (
                      <span className="draft-meta">
                        {v.created_by_name ? `· ${v.created_by_name} ` : ''}
                        · {formatDateTime(v.created_at)}
                      </span>
                    )}
                  </div>
                );
              })}
              {editionId && (
                <div className="draft-tab new" title="Vytvořit nový draft z aktivní verze" onClick={handleOpenDraftModal}>
                  + Nový draft
                </div>
              )}
            </div>

            {/* Indikační lišta — aktivní verze je read-only */}
            {isViewingActive && (
              <div className="active-info">
                <span className="badge text-bg-success">Aktivní verze</span>
                <div>
                  <span className="text-secondary">
                    Tato verze je publikovaná a slouží jen pro náhled. Úpravy provádějte přes draft.
                  </span>
                </div>
                <div className="info-actions">
                  <Button variant="primary" size="sm" onClick={handleOpenDraftModal}>
                    <i className="bi bi-plus-lg me-1" />Nový draft
                  </Button>
                </div>
              </div>
            )}

            {/* Indikační lišta - aktivní draft */}
            {currentVersion && currentVersion.type === 'draft' && (
              <div className="draft-info">
                <span className="badge text-bg-warning">Draft</span>
                <div>
                  <strong>{currentVersion.name}</strong>
                  <span className="text-secondary">
                    {currentVersion.created_by_name ? ` · vytvořil ${currentVersion.created_by_name}` : ''}
                    {currentVersion.created_at ? ` · ${formatDateTime(currentVersion.created_at)}` : ''}
                    {' '}· změny se neprojeví, dokud nepublikujete
                  </span>
                </div>
                <div className="info-actions">
                  <Button variant="success" size="sm" onClick={handlePublishDraft}>Publikovat</Button>
                  <Button variant="outline-danger" size="sm" onClick={handleDiscardDraft}>Zahodit</Button>
                </div>
              </div>
            )}

            {/* Info nad mřížkou */}
            <div className="d-flex flex-wrap align-items-center gap-3 small text-secondary" style={{ marginBottom: '1.125rem' }}>
              {!isViewingActive && (
                <Button variant="primary" size="sm" onClick={openCreatePageModal} disabled={!activeVersionId}>
                  <i className="bi bi-plus-lg me-1" />Přidat stranu
                </Button>
              )}
              <span>
                <strong className="text-dark">{pages.length} stran</strong>
              </span>
              <span className="ms-auto">
                <span className="legend-row">
                  <span className="lg-item"><span className="lg-swatch placena"></span>Placená</span>
                  <span className="lg-item"><span className="lg-swatch barter"></span>Barter</span>
                  <span className="lg-item"><span className="lg-swatch rezervace"></span>Rezervace</span>
                  <span className="lg-item"><span className="lg-swatch vyplnova"></span>Výplň</span>
                  <span className="lg-item"><span className="lg-swatch storno"></span>Storno</span>
                </span>
              </span>
            </div>

            {/* ============ MŘÍŽKA STRAN ============ */}
            {loadingEdition ? (
              <div className="text-center text-secondary py-5">Načítám vydání…</div>
            ) : pages.length === 0 ? (
              <div className="pages-grid">
                <div
                  className="thumb thumb-placeholder shadow-sm"
                  onClick={handleOpenCloneModal}
                  style={{ width: `${THUMB_PX_W}px`, height: `${THUMB_PX_W * 297 / 210}px` }}
                >
                  <div className="placeholder-inner">
                    <i className="bi bi-files" />
                    <div className="mt-2">Duplikovat<br /><small className="text-secondary">z jiného projektu</small></div>
                  </div>
                </div>
                <div
                  className="thumb thumb-placeholder shadow-sm"
                  onClick={openCreatePageModal}
                  style={{ width: `${THUMB_PX_W}px`, height: `${THUMB_PX_W * 297 / 210}px` }}
                >
                  <div className="placeholder-inner">
                    <i className="bi bi-plus-lg" />
                    <div className="mt-2">Vytvořit první stranu</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="pages-grid">
                {pages.map(renderPage)}
              </div>
            )}
          </main>
        </div>
        </div>
        {!allSelected && (
          <div className="spiglovani-empty-overlay">
            <div className="text-center">
              <i className="bi bi-grid-3x3 mb-2" style={{ fontSize: '2.5rem', color: '#00427A' }} />
              <div className="empty-title">Vyberte vydání</div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* ── Page modal (create / edit) ──────────────────────────────────── */}
      <Modal show={!!pageModal} onHide={() => setPageModal(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {pageModal?.mode === 'create' ? 'Nová strana' : `Strana ${pageModal?.page?.page_number ?? ''}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pageModal && (
            <Form>
              <Row className="g-2">
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>ID (název)</Form.Label>
                    <Form.Control
                      type="text"
                      value={pageModal.page.name ?? ''}
                      onChange={(e) => handlePageFieldChange('name', e.target.value)}
                      placeholder="Volitelný popisek (např. titulka, double)"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Typ stránky</Form.Label>
                    <Form.Check
                      type="checkbox"
                      id="page-is-complete"
                      label="Kompletová strana"
                      checked={!!pageModal.page.is_complete_page}
                      onChange={(e) => handlePageFieldChange('is_complete_page', e.target.checked)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <hr />
              <h6 className="text-secondary small">Rozměry &amp; mřížka</h6>
              <Row className="g-2">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="small">Šířka (mm)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      value={pageModal.page.width}
                      onChange={(e) => handlePageFieldChange('width', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="small">Výška (mm)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      value={pageModal.page.height}
                      onChange={(e) => handlePageFieldChange('height', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="small">Mezera mezi sloupci (mm)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      value={pageModal.page.column_gap}
                      onChange={(e) => handlePageFieldChange('column_gap', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <hr />
              <h6 className="text-secondary small">Okraje (mm)</h6>
              <Row className="g-2">
                {['margin_top', 'margin_right', 'margin_bottom', 'margin_left'].map(f => (
                  <Col md={3} key={f}>
                    <Form.Group>
                      <Form.Label className="small">{ {
                        margin_top: 'Horní', margin_right: 'Pravý', margin_bottom: 'Spodní', margin_left: 'Levý',
                      }[f]}</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        value={pageModal.page[f]}
                        onChange={(e) => handlePageFieldChange(f, e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>

              <hr />
              <Row className="g-2">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Barva stránky</Form.Label>
                    <Form.Control
                      type="color"
                      value={pageModal.page.color || '#ffffff'}
                      onChange={(e) => handlePageFieldChange('color', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <hr />
              <h6 className="text-secondary small">Inzerce</h6>
              <Row className="g-2 align-items-center">
                <Col md={12}>
                  <Form.Check
                    type="checkbox"
                    id="page-is-ad"
                    label="Inzertní strana"
                    checked={!!pageModal.page.is_ad_page}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setPageModal(prev => prev ? {
                        ...prev,
                        page: {
                          ...prev.page,
                          is_ad_page: checked,
                          ad_coverage_percent: checked && !Number(prev.page.ad_coverage_percent)
                            ? 100
                            : prev.page.ad_coverage_percent,
                        },
                      } : prev);
                    }}
                  />
                </Col>
                {!!pageModal.page.is_ad_page && (
                  <Col md={12} className="mt-2">
                    <Form.Label className="small d-flex justify-content-between">
                      <span>Pokrytí inzerce</span>
                      <strong>{Number(pageModal.page.ad_coverage_percent) || 0} %</strong>
                    </Form.Label>
                    <Form.Range
                      min={0}
                      max={100}
                      step={1}
                      value={Number(pageModal.page.ad_coverage_percent) || 0}
                      onChange={(e) => handlePageFieldChange('ad_coverage_percent', e.target.value)}
                    />
                  </Col>
                )}
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex">
          {pageModal?.mode === 'edit' && (
            <Button variant="outline-danger" className="me-auto" onClick={handleDeletePage}>
              <i className="bi bi-trash me-1" />Smazat
            </Button>
          )}
          <Button variant="outline-secondary" onClick={() => setPageModal(null)}>Zrušit</Button>
          <Button variant="primary" onClick={handleSavePage}>
            {pageModal?.mode === 'create' ? 'Vytvořit' : 'Uložit'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Move-page modal ─────────────────────────────────────────────── */}
      <Modal show={!!moveModal} onHide={() => setMoveModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Přesunout stranu {moveModal?.page?.page_number}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {moveModal && (
            <Form.Group>
              <Form.Label>Nová pozice (1 – {pages.length})</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={pages.length}
                step={1}
                value={moveModal.newPosition}
                onChange={(e) => setMoveModal(m => m ? { ...m, newPosition: e.target.value } : m)}
                autoFocus
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setMoveModal(null)}>Zrušit</Button>
          <Button variant="primary" onClick={handleConfirmMove}>Přesunout</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Clone-from-project modal ──────────────────────────────────── */}
      <Modal show={cloneModalOpen} onHide={() => !cloneSubmitting && setCloneModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Duplikovat strany z jiného projektu</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Zdrojový projekt</Form.Label>
            <SearchableSelect
              options={cloneSourceOptions}
              value={cloneSourceProjectId}
              onChange={(id) => setCloneSourceProjectId(id)}
              placeholder="Hledat projekt…"
              emptyLabel={
                cloneSourceLoading ? 'Načítám…' :
                cloneSourceOptions.length === 0 ? 'Žádné další projekty v médiu' :
                '— vyberte —'
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setCloneModalOpen(false)} disabled={cloneSubmitting}>Zrušit</Button>
          <Button
            variant="primary"
            onClick={handleConfirmClone}
            disabled={!cloneSourceProjectId || cloneSubmitting}
          >
            {cloneSubmitting ? 'Vytvářím…' : 'Vytvořit draft'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Draft create modal ─────────────────────────────────────────── */}
      <Modal show={draftModalOpen} onHide={() => setDraftModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nový draft</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Název draftu</Form.Label>
            <Form.Control
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              autoFocus
            />
            <Form.Text>Vytvoří se kopie aktuální aktivní verze (stránky i pozice inzerátů).</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDraftModalOpen(false)}>Zrušit</Button>
          <Button variant="primary" onClick={handleCreateDraft} disabled={!draftName.trim()}>Vytvořit</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Confirm dialog ─────────────────────────────────────────────── */}
      <Modal show={!!confirmDialog} onHide={() => setConfirmDialog(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmDialog?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmDialog?.body}</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmDialog(null)}>Zrušit</Button>
          <Button variant={confirmDialog?.variant || 'primary'} onClick={confirmDialog?.onConfirm}>
            {confirmDialog?.confirmLabel || 'Potvrdit'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Spiglovani;
